// src/components/home/HiRiseProjects/HiRiseProjects.jsx
import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { BsBuildings } from "react-icons/bs";

import {
  FiMapPin,
  FiArrowUpRight,
  FiHome,
  FiLayers,
  FiWind,
} from "react-icons/fi";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination, Autoplay } from "swiper/modules";

import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import "./HiRiseProjects.css";
import apis from "../../utils/apis";
import httpAction from "../../utils/httpAction";
import convertAmount from "../../utils/convertAmount";
import { useNavigate } from "react-router-dom";
import whatsappLink from "../../utils/whatsappLink";
import visit from "../../utils/visit";

// Swiper styles (required)

const PROJECTS = [
  {
    id: 1,
    status: "Pre-Launch",
    name: "HB Heights",
    location: "Gulberg, Lahore",
    startingFrom: "PKR 2.9 Cr",
    delivery: "Q4 2026",
    floors: 28,
    units: "Studio • 1 Bed • 2 Bed",
    highlights: ["Sky Lounge", "Smart Access", "Valet Parking"],
    progress: 12,
    image:
      "https://images.unsplash.com/photo-1496307653780-42ee777d4833?auto=format&fit=crop&w=1600&q=80",
  },
  {
    id: 2,
    status: "Under Construction",
    name: "Skyline Residences",
    location: "DHA Phase 6, Lahore",
    startingFrom: "PKR 4.2 Cr",
    delivery: "Q2 2026",
    floors: 34,
    units: "2 Bed • 3 Bed",
    highlights: ["Infinity Pool", "Gym", "Covered Parking"],
    progress: 48,
    image:
      "https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?auto=format&fit=crop&w=1600&q=80",
  },
  {
    id: 3,
    status: "Ready",
    name: "Central Tower",
    location: "MM Alam, Lahore",
    startingFrom: "PKR 6.8 Cr",
    delivery: "Ready to Move",
    floors: 22,
    units: "Office • Retail",
    highlights: ["Prime Frontage", "High Footfall", "Security"],
    progress: 100,
    image:
      "https://images.unsplash.com/photo-1501183638710-841dd1904471?auto=format&fit=crop&w=1600&q=80",
  },
  {
    id: 4,
    status: "Under Construction",
    name: "Lakeview Heights",
    location: "Bahria Town, Lahore",
    startingFrom: "PKR 3.5 Cr",
    delivery: "Q1 2027",
    floors: 30,
    units: "1 Bed • 2 Bed • 3 Bed",
    highlights: ["Lake View", "Community Deck", "Kids Area"],
    progress: 32,
    image:
      "https://images.unsplash.com/photo-1505691723518-36a5ac3b2e25?auto=format&fit=crop&w=1600&q=80",
  },
  {
    id: 5,
    status: "Pre-Launch",
    name: "Aurora Vertical",
    location: "Johar Town, Lahore",
    startingFrom: "PKR 2.2 Cr",
    delivery: "Q3 2027",
    floors: 26,
    units: "Studio • 1 Bed",
    highlights: ["Co-working", "Café", "Smart Elevators"],
    progress: 8,
    image:
      "https://images.unsplash.com/photo-1484154218962-a197022b5858?auto=format&fit=crop&w=1600&q=80",
  },
];

const FILTERS = ["Pre-Launch", "Under Construction", "Ready"];

function statusClass(status) {
  if (status === "ready") return "ready";
  if (status === "under construction") return "uc";
  return "pre";
}

function HighlightsViewer({ item }) {
  return (
    <Swiper
      modules={[Autoplay]}
      slidesPerView={1}
      loop
      speed={700} // transition speed
      autoplay={{
        delay: 2500,
        disableOnInteraction: false,
        pauseOnMouseEnter: false,
      }}
      allowTouchMove={false} // set true if you want swipe with finger/mouse
      pagination={false}
      navigation={false}
    >
      {item?.map((s) => (
        <SwiperSlide key={s}>
          <div style={{ padding: "0.4rem 0" }}>
            <span className="hrChip">{s}</span>
          </div>
        </SwiperSlide>
      ))}
    </Swiper>
  );
}

function ProjectCard({ p }) {
  const addVisit = async () => {
    const data = {
      url: apis().lisitingAddVisit + "?id=" + p?._id,
      method: "PUT",
    };
    await httpAction(data);
  };

  const navigate = useNavigate();
  return (
    <div className="hrCard">
      <div className="hrCard__media">
        <img
          src={apis().localUrl + p?.data?.images[0]?.url}
          alt={p?.data?.title}
          loading="lazy"
        />
        <div className={`hrCard__status ${statusClass(p?.data?.construction)}`}>
          {p?.data?.construction}
        </div>

        <div className="hrCard__overlay">
          <div className="hrCard__priceRow">
            <div className="hrCard__priceLabel">Starting from</div>
            <div className="hrCard__price">
              {convertAmount(p?.data?.price)}/SQ.Ft
            </div>
          </div>

          <div className="hrCard__name">{p?.data?.title}</div>

          <div className="hrCard__loc">
            <FiMapPin />
            <span>{p?.data?.location}</span>
          </div>
        </div>
      </div>

      <div className="hrCard__body">
        <div className="hrMetaRow">
          <div className="hrMeta">
            <FiLayers />
            <span>{p?.data?.floors} Floor</span>
          </div>
          <div className="hrMeta">
            <FiHome />
            <span>{p?.data?.units} Unit</span>
          </div>
          <div className="hrMeta">
            <BsBuildings />
            <span>{p?.data?.towers} Tower</span>
          </div>
          <div className="hrMeta">
            <FiWind />
            <span>Deliver {p?.data?.completionDate}</span>
          </div>
        </div>

        <div className="hrChips">
          <HighlightsViewer item={p?.data?.highlights} />
        </div>

        <div className="hrProgress">
          <div className="hrProgress__top">
            <span>Construction Progress</span>
            <strong>{p?.data?.constructionProgress}%</strong>
          </div>
          <div className="hrProgress__bar">
            <div
              className="hrProgress__fill"
              style={{ width: `${p?.data?.constructionProgress}%` }}
            />
          </div>
        </div>

        <button
          onClick={() => {
            navigate("/project/detail?id=" + p?._id);
            addVisit();
          }}
          className="hrCard__btn"
          type="button"
        >
          View Project <FiArrowUpRight />
        </button>
      </div>
    </div>
  );
}

export default function HiRiseProjects() {
  const [active, setActive] = useState("Under Construction");
  const [list, setList] = useState([]);

  const addCall = async (id) => {
    visit();
  };

  const getProjects = async () => {
    const data = {
      url: apis().listingProjectList + "?status=" + active?.toLowerCase(),
    };
    const result = await httpAction(data);
    if (result?.status) {
      setList(result?.list);
    }
  };
  useEffect(() => {
    getProjects();
  }, [active]);

  const filtered = useMemo(() => {
    if (active === "All") return PROJECTS;
    return PROJECTS.filter((p) => p.status === active);
  }, [active]);

  const navigate = useNavigate();

  return (
    <section className="hr">
      <div className="hr__bg" aria-hidden="true" />
      <div className="container hr__container">
        <div className="row  g-4">
          {/* Left intro */}
          <div className="col-12 col-lg-4">
            <motion.div
              className="hr__intro"
              initial={{ opacity: 0, y: 14 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.35 }}
              transition={{ duration: 0.5, ease: "easeOut" }}
            >
              <span className="hr__kicker">Projects</span>
              <h2 className="hr__title">Signature Hi-Rise Projects</h2>
              <p className="hr__sub">
                Explore premium towers with modern amenities, prime locations,
                and investment-friendly plans — curated for lifestyle & value.
              </p>

              <div className="hr__filters">
                {FILTERS.map((f) => (
                  <button
                    key={f}
                    type="button"
                    className={`hrPill ${active === f ? "isActive" : ""}`}
                    onClick={() => {
                      setActive(f);
                    }}
                  >
                    {f}
                  </button>
                ))}
              </div>

              <div className="hr__ctaRow">
                <button
                  onClick={() => navigate("/projects")}
                  className="hrBtn hrBtn--primary"
                  type="button"
                >
                  View All Projects <FiArrowUpRight />
                </button>
                <button
                  onClick={addCall}
                  className="hrBtn hrBtn--ghost"
                  type="button"
                >
                  Get Investment Advice
                </button>
              </div>
            </motion.div>
          </div>

          {/* Right slider */}
          <div className="col-12 col-lg-8">
            <motion.div
              initial={{ opacity: 0, y: 18 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.25 }}
              transition={{ duration: 0.55, ease: "easeOut" }}
              className="hr__sliderWrap"
            >
              <Swiper
                modules={[Navigation, Pagination]}
                navigation
                pagination={{ clickable: true }}
                spaceBetween={16}
                slidesPerView={1}
                breakpoints={{
                  576: { slidesPerView: 1.1 },
                  768: { slidesPerView: 1.6 },
                  992: { slidesPerView: 2.1 },
                  1200: { slidesPerView: 2.3 },
                }}
                className="hr__swiper"
              >
                {list?.map((p) => (
                  <SwiperSlide key={p?._id}>
                    <ProjectCard p={p} />
                  </SwiperSlide>
                ))}
              </Swiper>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
}
